import { useCallback, useRef, useState } from "react";

import { useTranslations } from "next-intl";

import * as Dialog from "@/components/dialog/DialogLayout";
import { Icon } from "@/components/icons/Icon";
import { ActiveTooltip } from "@/components/tooltip/Tooltip";
import { twMerge } from "@/utils/twMerge";

import type { CropRect, ImageEdit } from "./utils/editImage";
import type { IconName } from "@/components/icons/Icon";
import type { PointerEvent } from "react";

const VIEWPORT_INSET = 56;
const ZOOM_STEP = 25;
const MIN_ZOOM = 25;
const MAX_ZOOM = 400;
// Wheel delta that zooms by a factor of e; one mouse wheel notch (~100) zooms about 40%.
const WHEEL_ZOOM_SCALE = 300;

// Figma's background blur radius 4 renders as a 2px CSS blur.
const PILL_CLASS_NAME =
  "flex items-center overflow-hidden rounded-10 bg-blue-300/40 backdrop-blur-[2px]";
const EDIT_PILL_CLASS_NAME =
  "flex items-center gap-1.5 p-1.5 rounded-10 border border-white/30 bg-blue-500 backdrop-blur-[2px] text-size-16 tracking-[-0.02em] text-white [--color-icon-main:var(--color-white)]";

type Size = { width: number; height: number };

/** Largest zoom (in % of the natural size) that fits the viewport, never upscaling past 100%. */
function getFitZoom({ width, height }: Size, rotation: number) {
  const [visibleWidth, visibleHeight] = rotation % 180 === 0 ? [width, height] : [height, width];
  if (!visibleWidth || !visibleHeight) return 100;

  const fit = Math.min(
    (window.innerWidth - VIEWPORT_INSET * 2) / visibleWidth,
    (window.innerHeight - VIEWPORT_INSET * 2) / visibleHeight,
  );
  return Math.min(100, Math.floor(fit * 100));
}

function ToolbarButton({
  icon,
  label,
  onClick,
  disabled,
  iconClassName,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  iconClassName?: string;
}) {
  return (
    <ActiveTooltip
      focusDisabled
      title={label}
      placement="bottom"
      disabled={disabled}
      className="flex"
    >
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        className="flex p-1.5 enabled:hover:bg-blue-300/60 disabled:opacity-40"
      >
        <Icon name={icon} width={24} height={24} className={iconClassName} />
      </button>
    </ActiveTooltip>
  );
}

type CropHandle = "move" | "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se";

const FULL_CROP: CropRect = { x: 0, y: 0, width: 1, height: 1 };

const CORNER_HANDLES = [
  { handle: "nw", className: "-left-1 -top-1 cursor-nwse-resize", path: "M2.5 12.5V2.5H12.5" },
  { handle: "ne", className: "-right-1 -top-1 cursor-nesw-resize", path: "M2.5 2.5H12.5V12.5" },
  { handle: "sw", className: "-left-1 -bottom-1 cursor-nesw-resize", path: "M2.5 2.5V12.5H12.5" },
  { handle: "se", className: "-right-1 -bottom-1 cursor-nwse-resize", path: "M12.5 2.5V12.5H2.5" },
] as const;

const EDGE_HANDLES = [
  {
    handle: "n",
    className: "left-1/2 -top-[3px] -translate-x-1/2 w-[30px] h-[5px] cursor-ns-resize",
  },
  {
    handle: "s",
    className: "left-1/2 -bottom-[3px] -translate-x-1/2 w-[30px] h-[5px] cursor-ns-resize",
  },
  {
    handle: "w",
    className: "top-1/2 -left-[3px] -translate-y-1/2 w-[5px] h-[30px] cursor-ew-resize",
  },
  {
    handle: "e",
    className: "top-1/2 -right-[3px] -translate-y-1/2 w-[5px] h-[30px] cursor-ew-resize",
  },
] as const;

const MIN_CROP_PX = 20;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function resizeCrop(start: CropRect, handle: CropHandle, dx: number, dy: number, min: Size) {
  if (handle === "move") {
    return {
      ...start,
      x: clamp(start.x + dx, 0, 1 - start.width),
      y: clamp(start.y + dy, 0, 1 - start.height),
    };
  }

  let { x, y, width, height } = start;
  if (handle.includes("w")) {
    x = clamp(start.x + dx, 0, start.x + start.width - min.width);
    width = start.x + start.width - x;
  }
  if (handle.includes("e")) {
    width = clamp(start.width + dx, min.width, 1 - start.x);
  }
  if (handle.includes("n")) {
    y = clamp(start.y + dy, 0, start.y + start.height - min.height);
    height = start.y + start.height - y;
  }
  if (handle.includes("s")) {
    height = clamp(start.height + dy, min.height, 1 - start.y);
  }
  return { x, y, width, height };
}

// Listens on the document so a drag keeps tracking outside the element and ends wherever it's released.
function trackDrag(
  onMove: (event: globalThis.PointerEvent) => void,
  isDone: (event: globalThis.PointerEvent) => boolean = () => true,
) {
  const onUp = (event: globalThis.PointerEvent) => {
    if (!isDone(event)) return;
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onUp);
    document.removeEventListener("pointercancel", onUp);
  };
  document.addEventListener("pointermove", onMove);
  document.addEventListener("pointerup", onUp);
  document.addEventListener("pointercancel", onUp);
}

/** Keeps zoom in range, letting it go as low as the fitted view of an image too big to fit at the minimum. */
function clampZoom(zoom: number, fitZoom: number) {
  return clamp(zoom, Math.min(MIN_ZOOM, fitZoom), MAX_ZOOM);
}

type Point = { x: number; y: number };

function pointerDistance(pointers: Map<number, Point>) {
  const [a, b] = [...pointers.values()];
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function isFullCrop({ x, y, width, height }: CropRect) {
  return x === 0 && y === 0 && width === 1 && height === 1;
}

function normalizeRotation(degrees: number) {
  return ((degrees % 360) + 360) % 360;
}

export function ImageViewer({
  src,
  alt,
  size,
  onClose,
  onDownload,
  onEdit,
  onEditStart,
}: {
  src: string;
  alt: string;
  size: Size;
  onClose: () => void;
  onDownload: () => void;
  /** Saves a rotation and crop to the document; omitted where the image can't be edited. */
  onEdit?: (edit: ImageEdit) => Promise<void>;
  /** Called when the user starts cropping or turning, so the image can load before Apply. */
  onEditStart?: () => void;
}) {
  const t = useTranslations("doc.image.viewer");
  const tCommon = useTranslations();
  const [naturalSize, setNaturalSize] = useState(size);
  const [rotation, setRotation] = useState(0);
  const [userZoom, setUserZoom] = useState<number | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [cropRect, setCropRect] = useState<CropRect | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyFailed, setApplyFailed] = useState(false);

  const activePointers = useRef(new Map<number, Point>());
  const pinchStart = useRef<{ distance: number; zoom: number } | null>(null);
  const cropPointerId = useRef<number | null>(null);

  const fitZoom = getFitZoom(naturalSize, rotation);
  const zoom = userZoom ?? fitZoom;
  const pendingRotation = normalizeRotation(rotation);
  const isQuarterTurn = pendingRotation % 180 !== 0;
  const displayedSize = {
    width: ((isQuarterTurn ? naturalSize.height : naturalSize.width) * zoom) / 100,
    height: ((isQuarterTurn ? naturalSize.width : naturalSize.height) * zoom) / 100,
  };
  const canApply = !!onEdit && (pendingRotation !== 0 || (!!cropRect && !isFullCrop(cropRect)));

  const toggleCrop = () => {
    if (isApplying) return;
    setApplyFailed(false);
    if (cropRect) {
      setCropRect(null);
      return;
    }
    setUserZoom(null);
    setOffset({ x: 0, y: 0 });
    setCropRect(FULL_CROP);
    onEditStart?.();
  };

  let applyStatus = t("apply");
  if (isApplying) applyStatus = t("applying");
  else if (applyFailed) applyStatus = t("applyFailed");

  /** Saves the rotation and crop, then closes; on failure keeps everything so the user can retry. */
  const apply = async () => {
    if (!onEdit || isApplying) return;
    setIsApplying(true);
    setApplyFailed(false);
    try {
      await onEdit({ rotation: pendingRotation, rect: cropRect ?? FULL_CROP });
    } catch (error) {
      console.error("Failed to edit image", error);
      setApplyFailed(true);
      return;
    } finally {
      setIsApplying(false);
    }
    onClose();
  };

  const handleClose = () => {
    if (!isApplying) onClose();
  };

  const handleCropPointerDown = (handle: CropHandle) => (event: PointerEvent<Element>) => {
    if (!cropRect || isApplying) return;
    event.preventDefault();
    event.stopPropagation();
    // The first finger owns the drag; others would make the crop jump or end it early.
    if (cropPointerId.current !== null) return;
    const { pointerId } = event;
    cropPointerId.current = pointerId;

    const start = cropRect;
    const { clientX: startX, clientY: startY } = event;
    const min = {
      width: Math.min(1, MIN_CROP_PX / displayedSize.width),
      height: Math.min(1, MIN_CROP_PX / displayedSize.height),
    };

    trackDrag(
      (moveEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        const dx = (moveEvent.clientX - startX) / displayedSize.width;
        const dy = (moveEvent.clientY - startY) / displayedSize.height;
        setCropRect(resizeCrop(start, handle, dx, dy, min));
      },
      (upEvent) => {
        if (upEvent.pointerId !== pointerId) return false;
        cropPointerId.current = null;
        return true;
      },
    );
  };

  const zoomIn = () =>
    setUserZoom(Math.min(MAX_ZOOM, Math.floor(zoom / ZOOM_STEP + 1) * ZOOM_STEP));
  const zoomOut = () =>
    setUserZoom(Math.max(MIN_ZOOM, Math.ceil(zoom / ZOOM_STEP - 1) * ZOOM_STEP));

  const rotate = (degrees: number) => {
    if (isApplying) return;
    onEditStart?.();
    setRotation((current) => current + degrees);
    setOffset({ x: 0, y: 0 });
    setApplyFailed(false);
    // The crop region is relative to the turned image, so a new turn starts it over.
    if (cropRect) setCropRect(FULL_CROP);
  };

  // Native listener: React's wheel handler is passive, and a trackpad pinch (ctrl+wheel) would zoom the page.
  const listenForWheelZoom = useCallback(
    (overlay: HTMLDivElement) => {
      const onWheel = (event: WheelEvent) => {
        event.preventDefault();
        setUserZoom((current) =>
          clampZoom((current ?? fitZoom) * Math.exp(-event.deltaY / WHEEL_ZOOM_SCALE), fitZoom),
        );
      };
      const { ownerDocument } = overlay;
      ownerDocument.addEventListener("wheel", onWheel, { passive: false });
      return () => ownerDocument.removeEventListener("wheel", onWheel);
    },
    [fitZoom],
  );

  /** One pointer pans the image, two pinch to zoom. */
  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const pointers = activePointers.current;
    // A pan starts on the image, but the second finger of a pinch can land anywhere.
    if (!pointers.size && !(event.target instanceof HTMLImageElement)) return;

    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.size === 2) pinchStart.current = { distance: pointerDistance(pointers), zoom };
    if (pointers.size > 1) return;

    trackDrag(
      (moveEvent) => {
        const last = pointers.get(moveEvent.pointerId);
        if (!last) return;
        pointers.set(moveEvent.pointerId, { x: moveEvent.clientX, y: moveEvent.clientY });

        const pinch = pinchStart.current;
        if (pinch) {
          setUserZoom(
            clampZoom((pinch.zoom * pointerDistance(pointers)) / pinch.distance, fitZoom),
          );
          return;
        }
        setOffset((current) => ({
          x: current.x + moveEvent.clientX - last.x,
          y: current.y + moveEvent.clientY - last.y,
        }));
      },
      (upEvent) => {
        pointers.delete(upEvent.pointerId);
        pinchStart.current = null;
        return pointers.size === 0;
      },
    );
  };

  return (
    <Dialog.Root
      open
      onClose={handleClose}
      aria-label={alt}
      // Captured so Enter saves even while a toolbar button still has focus from the click.
      onKeyDownCapture={(event) => {
        if (event.key !== "Enter" || !canApply) return;
        event.preventDefault();
        event.stopPropagation();
        void apply();
      }}
    >
      <Dialog.Backdrop className="bg-black/60" />
      {/* Escape (handled by Dialog) is the keyboard equivalent of clicking outside the image */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={listenForWheelZoom}
        data-testid="image-viewer-overlay"
        className="fixed inset-0 flex items-center justify-center overflow-hidden touch-none"
        onPointerDown={cropRect ? undefined : handlePointerDown}
        onClick={(event) => {
          // A crop drag released outside the image lands here, so it must not close the viewer.
          if (event.target === event.currentTarget && !cropRect) handleClose();
        }}
      >
        <img
          src={src || undefined}
          alt={alt}
          draggable={false}
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget;
            if (naturalWidth && naturalHeight) {
              setNaturalSize({ width: naturalWidth, height: naturalHeight });
            }
          }}
          className={twMerge(
            "max-w-none shrink-0 select-none touch-none",
            !cropRect && "cursor-grab active:cursor-grabbing",
          )}
          style={{
            width: naturalSize.width || undefined,
            height: naturalSize.height || undefined,
            transform: `translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${zoom / 100})`,
          }}
        />
      </div>
      {cropRect && (
        <div
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ width: displayedSize.width, height: displayedSize.height }}
        >
          <div
            data-testid="image-crop-area"
            role="presentation"
            onPointerDown={handleCropPointerDown("move")}
            className="absolute touch-none cursor-move shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"
            style={{
              left: `${cropRect.x * 100}%`,
              top: `${cropRect.y * 100}%`,
              width: `${cropRect.width * 100}%`,
              height: `${cropRect.height * 100}%`,
            }}
          >
            {CORNER_HANDLES.map(({ handle, className, path }) => (
              <svg
                key={handle}
                data-testid={`crop-handle-${handle}`}
                width={15}
                height={15}
                viewBox="0 0 15 15"
                fill="none"
                onPointerDown={handleCropPointerDown(handle)}
                className={twMerge("absolute", className)}
              >
                <path
                  d={path}
                  stroke="white"
                  strokeWidth={5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ))}
            {EDGE_HANDLES.map(({ handle, className }) => (
              <div
                key={handle}
                data-testid={`crop-handle-${handle}`}
                role="presentation"
                onPointerDown={handleCropPointerDown(handle)}
                className={twMerge("absolute rounded-[3px] bg-white", className)}
              />
            ))}
          </div>
        </div>
      )}
      <div className="fixed top-2.5 right-2.5 flex items-center gap-2.5 sm:left-2.5 sm:flex-wrap sm:justify-end sm:gap-1.5 [--color-icon-main:var(--color-white)] [--color-icon-secondary:var(--color-white)]">
        {canApply && (
          <button
            type="button"
            onClick={() => void apply()}
            disabled={isApplying}
            className={twMerge(EDIT_PILL_CLASS_NAME, "px-3 disabled:opacity-60")}
          >
            {applyStatus}
          </button>
        )}
        <div className={twMerge(PILL_CLASS_NAME, "gap-2.5")}>
          <ToolbarButton
            icon="Minus"
            label={t("zoomOut")}
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
          />
          <span className="min-w-11 text-center text-size-16 text-white">{Math.round(zoom)}%</span>
          <ToolbarButton
            icon="PlusLarge"
            label={t("zoomIn")}
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
          />
        </div>
        <div className={twMerge(PILL_CLASS_NAME, "gap-1")}>
          <ToolbarButton icon="RotateLeft" label={t("rotateLeft")} onClick={() => rotate(-90)} />
          <ToolbarButton
            icon="RotateLeft"
            label={t("rotateRight")}
            onClick={() => rotate(90)}
            iconClassName="-scale-x-100"
          />
        </div>
        {onEdit && (
          <div className={PILL_CLASS_NAME}>
            <ToolbarButton icon="Crop" label={t("crop")} onClick={toggleCrop} />
          </div>
        )}
        <div className={PILL_CLASS_NAME}>
          <ToolbarButton icon="download/Download" label={t("save")} onClick={onDownload} />
        </div>
        <div className={PILL_CLASS_NAME}>
          <ToolbarButton icon="Cross" label={tCommon("dialog.close")} onClick={handleClose} />
        </div>
      </div>
    </Dialog.Root>
  );
}
